import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  PageDS,
  usePersistMuiTableState,
} from "@qivia/ui";
import { getDepartmentsByCompanyAsync, selectDepartments, selectGetDepartmentsByCompanyStatus } from "./departmentsSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { DepartmentType, SidePanelDepartmentTypeDisplayed } from "./departmentsApi";
import { useTranslation } from "react-i18next";
import { selectCompany } from "../homeSlice";
import { useSearchParams } from "react-router-dom";

type DepartmentRow = {
  name: string;
  vehiclesCount: number;
};

interface ShareInterfaceDepartmentsProps {
  cta: {
    title: string;
    action: () => void;
    leftIcon: JSX.Element | null;
  };
  sidePanelAction: (props: Partial<SidePanelDepartmentTypeDisplayed>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceDepartments = (
  props: ShareInterfaceDepartmentsProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [listDisplayed, setListDisplayed] = useState<DepartmentRow[] | null>(
    null,
  );

  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );

  const [searchParams] = useSearchParams();
  const fromNavigate = searchParams.get("fromNavigate") === "true";

  const hasExecutedFromNavigate = useRef(false);
  const shouldExecuteFromNavigate = useRef(false);

  if (fromNavigate && !shouldExecuteFromNavigate.current) {
    shouldExecuteFromNavigate.current = true;
  }

  const apiRef = useGridApiRef();
  const pageName = "departments";
  const key = "departments-grid";
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName
  );

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [company, dispatch, getDepartmentsByCompanyStatus]);

  const departmentsList: DepartmentRow[] = useMemo(() => {

    return departments.map((department: DepartmentType) => ({
      id: department.uuid,
      name: department.name,
      vehiclesCount: department.vehiclesCount,
    }));
  }, [departments]);

  useEffect(() => {
    if (getDepartmentsByCompanyStatus === "success") {
      setListDisplayed(departmentsList);
    }
  }, [departmentsList, departments, getDepartmentsByCompanyStatus]);

    // Only after a navigate from an other page like transaction, we select the row to send to the top of the MaterialUiTable
    useEffect(() => {
      if (
        listDisplayed &&
        shouldExecuteFromNavigate.current &&
        !hasExecutedFromNavigate.current
      ) {
        hasExecutedFromNavigate.current = true;
        shouldExecuteFromNavigate.current = false;
      }
    }, [listDisplayed, apiRef]);

  const columnsDisplayed: {
    [index in keyof DepartmentRow]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: { type: "string", width: 250 },
      vehiclesCount: { type: "number", width: 150, align: "left", headerAlign: "left", renderCell: (params) => (
        <span style={{ width: "100%", textAlign: "left", display: "block" }}>
          {params.value as number ?? 0}
        </span>
      ), },
    };
  }, []);

  const searchBarProps = useMemo(
    () => ({
      values: departmentsList,
      setFilterValues: setListDisplayed,
      keysToIgnore: ["id" as const],
    }),
    [departmentsList],
  );

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <PageDS title={t("departments.title")} cta={props.cta}>
      <MaterialUiTable<keyof DepartmentRow, DepartmentRow>
        rows={listDisplayed}
        columns={columnsDisplayed}
        onRowClickAction={props.sidePanelAction}
        apiRef={apiRef}
        dataGridState={dataGridState}
        pageName={pageName}
        searchBar={searchBarProps}
      />
      {props.sidePanel ? props.sidePanel : <></>}
      </PageDS>
    </>
  );
};
